<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100">HomeBase Signups</h1>

    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Signups</h2>
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <select
          v-model="filters.statusMode"
          aria-label="Filter by status"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="any">Any</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="awaiting_deployment">Awaiting Deployment</option>
        </select>

        <input
          v-model="filters.community"
          type="text"
          placeholder="Community"
          aria-label="Filter by community"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
        />

        <input
          v-model="filters.search"
          type="text"
          placeholder="Search name/phone/email"
          aria-label="Search by name, phone or email"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200 flex-1 min-w-[200px]"
        />

        <div class="flex items-center gap-2">
          <label for="signups-date-from" class="text-sm font-medium text-neutral-700 dark:text-neutral-300">From</label>
          <input
            id="signups-date-from"
            v-model="filters.dateFrom"
            type="date"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
          />
        </div>

        <div class="flex items-center gap-2">
          <label for="signups-date-to" class="text-sm font-medium text-neutral-700 dark:text-neutral-300">To</label>
          <input
            id="signups-date-to"
            v-model="filters.dateTo"
            type="date"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
          />
        </div>

        <button
          @click="reload"
          class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Apply Filters
        </button>
      </div>

      <AlertBanner
        v-if="error && items.length"
        type="error"
        message="Couldn't refresh signups — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />
      <AlertBanner
        v-else-if="!listLoading && total && items.length < total"
        type="info"
        :message="`Showing the first ${items.length} of ${total} signups — narrow your filters to see the rest.`"
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="listLoading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading signups...</p>
        </div>
      </div>

      <!-- Error State (nothing loaded yet) -->
      <ErrorState
        v-else-if="error && !items.length"
        title="Couldn't load signups"
        :message="error"
        @retry="reload"
      />

      <!-- Empty State -->
      <div v-else-if="!sortedAndFiltered.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No signups found</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  :indeterminate="selected.length > 0 && selected.length < sortedAndFiltered.length"
                  @change="toggleAll"
                  aria-label="Select all signups"
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800"
                />
              </th>
              <th :aria-sort="ariaSort('fullName')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('fullName')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Name
                  <span v-if="sortState.key === 'fullName'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('phone')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('phone')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Phone
                  <span v-if="sortState.key === 'phone'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('email')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('email')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Email
                  <span v-if="sortState.key === 'email'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('community')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('community')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Community
                  <span v-if="sortState.key === 'community'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('villaNumber')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('villaNumber')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Villa
                  <span v-if="sortState.key === 'villaNumber'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('status')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('status')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Status
                  <span v-if="sortState.key === 'status'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('createdAt')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('createdAt')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Created
                  <span v-if="sortState.key === 'createdAt'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="s in sortedAndFiltered" :key="s.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  :value="s.id"
                  v-model="selected"
                  :aria-label="`Select signup ${s.fullName || s.id}`"
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800"
                />
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ s.fullName }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ s.phone }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ s.email }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ s.community }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ s.villaNumber }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ humanize(s.status) }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ (s.createdAt || '').slice(0,10) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center gap-4 mt-6">
        <button
          @click="openDeployConfirm"
          :disabled="!selected.length || requesting"
          class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Request Deployment
        </button>

        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          Total: {{ total }}
        </span>
      </div>
    </div>

    <ConfirmDialog
      :show="showDeployConfirm"
      title="Request deployment?"
      :message="`Request deployment for ${selected.length} signup${selected.length === 1 ? '' : 's'}? They will be moved to the awaiting-deployment queue.`"
      confirm-label="Request Deployment"
      :danger="false"
      :loading="requesting"
      @confirm="confirmRequestDeployment"
      @cancel="showDeployConfirm = false"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import useHomebaseSignups from '@/composables/useHomebaseSignups'
import client from '@/composables/homebaseClient'
import useToast from '@/composables/useToast'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'

export default {
  name: 'HomeBaseSignups',
  components: { ErrorState, AlertBanner, ConfirmDialog },
  setup() {
    const { items, loading: listLoading, error: listError, total, params, fetchSignups } = useHomebaseSignups()

    const { showToast } = useToast()

    const filters = reactive({
      statusMode: 'any',
      community: '',
      search: '',
      dateFrom: '',
      dateTo: ''
    })

    const applyFilters = () => {
      params.value.status = filters.statusMode
      params.value.community = filters.community.trim()
      fetchSignups()
    }

    const reload = () => applyFilters()

    const sortState = reactive({ key: 'createdAt', dir: 'desc' })
    const sortBy = (key) => {
      if (sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        sortState.key = key
        sortState.dir = 'asc'
      }
    }

    const ariaSort = (key) =>
      sortState.key === key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'

    // 'awaiting_deployment' -> 'Awaiting Deployment'
    const humanize = (value) => {
      if (!value) return value
      return String(value)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    }

    const sortedAndFiltered = computed(() => {
      const q = filters.search.trim().toLowerCase()
      let list = items.value
      if (q) {
        list = list.filter(s =>
          (s.fullName || '').toLowerCase().includes(q) ||
          (s.phone || '').toLowerCase().includes(q) ||
          (s.email || '').toLowerCase().includes(q)
        )
      }
      // The API doesn't accept date filters yet, so apply them client-side
      // (ISO date strings compare lexicographically).
      if (filters.dateFrom) {
        list = list.filter(s => (s.createdAt || '').slice(0, 10) >= filters.dateFrom)
      }
      if (filters.dateTo) {
        list = list.filter(s => (s.createdAt || '').slice(0, 10) <= filters.dateTo)
      }
      return [...list].sort((a, b) => {
        const va = (a[sortState.key] || '')
        const vb = (b[sortState.key] || '')
        if (va < vb) return sortState.dir === 'asc' ? -1 : 1
        if (va > vb) return sortState.dir === 'asc' ? 1 : -1
        return 0
      })
    })

    const selected = ref([])
    const allSelected = computed(() => selected.value.length > 0 && selected.value.length === sortedAndFiltered.value.length)
    const toggleAll = (e) => {
      if (e.target.checked) selected.value = sortedAndFiltered.value.map(s => s.id)
      else selected.value = []
    }

    const showDeployConfirm = ref(false)
    const requesting = ref(false)

    const openDeployConfirm = () => {
      if (!selected.value.length) return
      showDeployConfirm.value = true
    }

    const confirmRequestDeployment = async () => {
      if (!selected.value.length || requesting.value) return

      requesting.value = true
      try {
        const res = await client.post('/signups/awaiting-deployment/batch', {
          signupIds: selected.value
        })
        const count = Array.isArray(res.data) ? res.data.length : selected.value.length
        selected.value = []
        showDeployConfirm.value = false
        await fetchSignups() // refresh the list
        showToast('success', `Deployment requested for ${count} signup${count === 1 ? '' : 's'}`)
      } catch (err) {
        console.error('Deployment request failed:', err)
        showToast('error', 'Failed to request deployment. Please try again.')
      } finally {
        requesting.value = false
      }
    }

    onMounted(() => {
      fetchSignups()
    })

    return {
      items, listLoading, error: listError, total,
      filters, reload, sortedAndFiltered, sortBy, sortState, ariaSort, humanize,
      selected, allSelected, toggleAll,
      showDeployConfirm, requesting, openDeployConfirm, confirmRequestDeployment
    }
  }
};
</script>
